/**
 * Header Context Menu
 * Right-click menu on the timeline header for toggling row visibility.
 * Menu stays open when toggling rows, closes on click-outside or Escape.
 */

import { useEffect, useCallback } from 'react';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface HeaderContextMenuProps {
  x: number;
  y: number;
  mode: 'auto' | 'manual';
  isJahreVisible: boolean;
  isMonateVisible: boolean;
  isTageVisible: boolean;
  onToggleJahre: () => void;
  onToggleMonate: () => void;
  onToggleTage: () => void;
  onSetAutoMode: () => void;
  onClose: () => void;
}

export function HeaderContextMenu({
  x, y,
  mode,
  isJahreVisible,
  isMonateVisible,
  isTageVisible,
  onToggleJahre,
  onToggleMonate,
  onToggleTage,
  onSetAutoMode,
  onClose,
}: HeaderContextMenuProps) {
  const { t } = useTranslation();
  // Close on click outside or Escape
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-header-context-menu]')) {
        onClose();
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const timer = setTimeout(() => {
      window.addEventListener('mousedown', handleClick);
      window.addEventListener('keydown', handleEscape);
    }, 0);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('mousedown', handleClick);
      window.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const handleAutoClick = useCallback(() => {
    onSetAutoMode();
  }, [onSetAutoMode]);

  return (
    <div
      data-header-context-menu
      style={{
        position: 'fixed',
        left: x,
        top: y,
        zIndex: 9999,
        backgroundColor: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        padding: 4,
        minWidth: 200,
      }}
    >
      {/* Auto mode toggle */}
      <MenuRow
        label={t('boardv3.headerMenu.auto')}
        checked={mode === 'auto'}
        onClick={handleAutoClick}
      />

      {/* Separator */}
      <div style={{ height: 1, backgroundColor: '#e5e7eb', margin: '4px 0' }} />

      {/* Row toggles */}
      <MenuRow label={t('boardv3.headerMenu.showYears')} checked={isJahreVisible} onClick={onToggleJahre} />
      <MenuRow label={t('boardv3.headerMenu.showMonths')} checked={isMonateVisible} onClick={onToggleMonate} />
      <MenuRow label={t('boardv3.headerMenu.showDays')} checked={isTageVisible} onClick={onToggleTage} />
    </div>
  );
}

function MenuRow({ label, checked, onClick }: { label: string; checked: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        padding: '6px 12px',
        fontSize: 13,
        color: '#1f2937',
        backgroundColor: 'transparent',
        border: 'none',
        borderRadius: 6,
        cursor: 'pointer',
        textAlign: 'left',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
    >
      <span style={{ width: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {checked && <Check size={14} style={{ color: '#3b82f6' }} />}
      </span>
      {label}
    </button>
  );
}
